import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  Edit,
  Loading,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  useGetList,
  useGetOne,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import CloudFrontImageField from 'src/components/CloudFrontImageField';
import { dataProvider } from 'src/providers/data-provider';

interface FormField {
  id: string;
  originalKey: string;
  displayName: string;
  value?: any;
  order: number;
}

interface FieldChange {
  originalKey: string;
  newKey: string;
  action: 'keep' | 'rename' | 'delete' | 'add';
  value?: any;
  order: number;
}

interface FormTemplateEditContentProps {
  fields: FormField[];
  deletedFields: FormField[];
  setFields: (fields: FormField[]) => void;
  setDeletedFields: (fields: FormField[]) => void;
  onAnalyzeChanges: () => FieldChange[];
  isDragEnabled: boolean;
  record: any;
  selectedImage: any;
  setSelectedImage: any;
}

const FormTemplateEditContent: React.FC<FormTemplateEditContentProps> = ({
  fields,
  deletedFields,
  setFields,
  setDeletedFields,
  onAnalyzeChanges,
  isDragEnabled,
  record,
  selectedImage,
  setSelectedImage,
}) => {
  const translate = useTranslate();
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const newFieldNameRef = useRef<HTMLInputElement | null>(null);
  const notify = useNotify();
  const [imagePreview, setImagePreview] = useState<any>(null);
  const handleFieldNameChange = (index: number, newName: string) => {
    const updatedFields = [...fields];
    updatedFields[index] = {
      ...updatedFields[index],
      displayName: newName,
    };
    setFields(updatedFields);
  };

  const handleAddField = () => {
    if (!newFieldName) {
      notify('Field name is required', { type: 'error' });
      return;
    }

    const timestamp = Date.now();
    const newId = `draggable-new-${timestamp}`;
    const newField = {
      id: newId,
      originalKey: newFieldName,
      displayName: newFieldName,
      order: fields.length,
    };

    setFields([...fields, newField]);
    setNewFieldName('');

    setShowFieldDialog(false);
  };

  const handleRemoveField = (index: number) => {
    const removedField = fields[index];
    if (!removedField.id.startsWith('draggable-new-')) {
      setDeletedFields([...deletedFields, removedField]);
    }
    const updatedFields = fields.filter((_, i) => i !== index);
    setFields(updatedFields);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const reorderedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    setFields(reorderedItems);
  };

  const handleImageChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setSelectedImage(file); // Store the file for later upload
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Box display="flex" gap={2} width={'100%'}>
        <Box flex={1}>
          <Box
            sx={{
              width: '400px',
              height: '192px',
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.300',
              mb: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {imagePreview ? (
              <Box sx={{ position: 'relative', height: '100%' }}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Template preview"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <label>
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        color: 'primary.main',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                    >
                      <CameraAltIcon />
                      <Typography>{translate('simple.reselect')}</Typography>
                      <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                    </Box>
                  </label>
                </Box>
              </Box>
            ) : record?.image_key ? (
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  // center
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloudFrontImageField record={record} source="image_key" />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <label>
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        color: 'primary.main',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                    >
                      <CameraAltIcon />
                      <Typography>{translate('simple.reselect')}</Typography>
                      <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                    </Box>
                  </label>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CameraAltIcon
                  sx={{
                    color: 'grey.400',
                    fontSize: 48,
                    mb: 1,
                  }}
                />
                <label>
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'common.white',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    <Typography>{translate('simple.select_a_photo')}</Typography>
                    <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                  </Box>
                </label>
              </Box>
            )}
          </Box>
          <TextInput
            source="name"
            validate={required()}
            fullWidth
            label={translate('resources.form_template.fields.name')}
          />
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {translate('resources.form_template.fields.form_fields')}
          </Typography>
          {isDragEnabled && fields.length > 0 && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable-fields">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} style={{ minHeight: '60px' }}>
                    {fields.map((field, index) => (
                      <Draggable key={field.id} draggableId={field.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              marginBottom: '8px',
                              padding: '8px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              background: snapshot.isDragging ? '#f0f0f0' : 'white',
                            }}
                          >
                            <Box display="flex" gap={1} alignItems="center">
                              <div {...provided.dragHandleProps}>
                                <DragIndicatorIcon color="action" />
                              </div>
                              <Box flexGrow={1}>
                                <TextInput
                                  source={`fields.${index}.name`}
                                  label={field.originalKey}
                                  defaultValue={field.displayName}
                                  onChange={(e) => handleFieldNameChange(index, e.target.value)}
                                  fullWidth
                                />
                              </Box>
                              <Button onClick={() => handleRemoveField(index)} color="error" size="small">
                                {translate('ra.action.delete')}
                              </Button>
                            </Box>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
          <Button variant="contained" onClick={() => setShowFieldDialog(true)} sx={{ mt: 2 }}>
            {translate('simple.add_new_field')}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={showFieldDialog}
        onClose={() => {
          setShowFieldDialog(false);
          setNewFieldName('');
        }}
      >
        <DialogTitle>{translate('simple.add_new_field')}</DialogTitle>
        <DialogContent>
          <TextInput
            ref={newFieldNameRef}
            source="newFieldName"
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
            label={translate('simple.new_field_name')}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFieldDialog(false)}>{translate('ra.action.cancel')}</Button>
          <Button onClick={handleAddField} color="primary">
            {translate('ra.action.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FormTemplateEdit = () => {
  const { id: recordId } = useParams();
  const [record, setRecord] = useState(null);
  const notify = useNotify();
  const redirect = useRedirect();
  const [fields, setFields] = useState<FormField[]>([]);
  const [deletedFields, setDeletedFields] = useState<FormField[]>([]);
  const [showChangeConfirm, setShowChangeConfirm] = useState(false);
  const [changes, setChanges] = useState<FieldChange[]>([]);
  const [isDragEnabled, setIsDragEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const translate = useTranslate();

  const { data: template, isLoading: isLoadingTemplate } = useGetOne('form_template', { id: recordId });

  const { data: formFields, isLoading: isLoadingFields } = useGetList(
    'form_field',
    {
      filter: { form_template_id: recordId },
      sort: { field: 'order', order: 'ASC' },
    },
    {
      enabled: !!recordId,
    },
  );

  useEffect(() => {
    if (template) {
      setRecord(template);
    }
  }, [template]);

  useEffect(() => {
    if (formFields) {
      const initialFields = formFields.map((field: any) => ({
        id: `draggable-${field.id}`,
        originalKey: field.field_name,
        displayName: field.field_name,
        value: '',
        order: field.order || 0,
      }));

      const sortedFields = [...initialFields].sort((a, b) => a.order - b.order);
      setFields(sortedFields);
      setIsDragEnabled(true);
    }
  }, [formFields]);

  const uploadTemplateImage = async (file: any) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const authParams = localStorage?.getItem('auth') || '';
      const { access_token } = JSON.parse(authParams);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/form/upload-template-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const data = await response.json();
      return data.image_key;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const analyzeChanges = () => {
    const changes: FieldChange[] = [];

    fields.forEach((field, index) => {
      if (field.id.startsWith('draggable-new-')) {
        changes.push({
          originalKey: field.originalKey,
          newKey: field.displayName,
          action: 'add',
          value: field.value,
          order: index,
        });
      } else if (field.originalKey === field.displayName) {
        changes.push({
          originalKey: field.originalKey,
          newKey: field.displayName,
          action: 'keep',
          value: field.value,
          order: index,
        });
      } else {
        changes.push({
          originalKey: field.originalKey,
          newKey: field.displayName,
          action: 'rename',
          value: field.value,
          order: index,
        });
      }
    });

    deletedFields.forEach((field) => {
      changes.push({
        originalKey: field.originalKey,
        newKey: field.displayName,
        action: 'delete',
        value: null,
        order: 0,
      });
    });

    return changes;
  };

  const saveFormTemplate = async (formData: any, fieldChanges: FieldChange[]) => {
    try {
      setIsSubmitting(true);

      let newImageKey = formData.imageKey;
      if (selectedImage) {
        newImageKey = await uploadTemplateImage(selectedImage);
      }

      if (!newImageKey) {
        notify('simple.image_is_required', { type: 'error' });
        return;
      }
      const fieldData = {
        kept: fieldChanges
          .filter((c) => c.action === 'keep')
          .map((f) => ({
            field_name: f.newKey,
            field_name_original: f.originalKey,
            field_type: 'text',
            order: f.order,
            form_template_id: recordId,
          })),
        renamed: fieldChanges
          .filter((c) => c.action === 'rename')
          .map((f) => ({
            field_name: f.newKey,
            field_name_original: f.originalKey,
            field_type: 'text',
            order: f.order,
            form_template_id: recordId,
          })),
        deleted: fieldChanges
          .filter((c) => c.action === 'delete')
          .map((f) => ({
            field_name: f.newKey,
            field_name_original: f.originalKey,
            field_type: 'text',
            order: f.order,
            form_template_id: recordId,
          })),
        added: fieldChanges
          .filter((c) => c.action === 'add')
          .map((f) => ({
            field_name: f.newKey,
            field_name_original: f.originalKey,
            field_type: 'text',
            order: f.order,
            form_template_id: recordId,
          })),
      };

      const response = await dataProvider.update('form_template', {
        id: recordId,
        data: {
          ...formData,
          id: recordId,
          imageKey: newImageKey,
          fields: fieldData,
        },
        previousData: formData,
        meta: {
          gqlQuery: `
          mutation RequestUpdateTemplate(
            $template_id: Int!, 
            $name: String, 
            $new_data: jsonb, 
            $imageKey: String
          ) {
            request_update_template(params: {
              template_id: $template_id,
              name: $name,
              new_data: $new_data,
              imageKey: $imageKey
            }) {
              error
            }
          }
        `,
          variables: {
            template_id: Number(recordId),
            name: formData.name,
            new_data: fieldData,
            imageKey: newImageKey,
          },
        },
      });

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      notify(translate('resources.form_template.notification.saved_success'), { type: 'success' });
      redirect('list', 'form_template');
      window.location.reload();
    } catch (error) {
      console.error('Save error:', error);
      notify(translate('resources.form_template.notification.saved_error'), { type: 'error' });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (formData: any) => {
    const fieldChanges = analyzeChanges();
    console.log({ formData });

    try {
      if (fieldChanges.some((c) => c.action !== 'keep')) {
        setChanges(fieldChanges);
        setShowChangeConfirm(true);
        return;
      }

      await saveFormTemplate(formData, fieldChanges);
    } catch (error) {
      console.error('Submit error:', error);
      notify('Error saving template', { type: 'error' });
    }
  };

  const handleSaveConfirmed = async (formData: any) => {
    try {
      await saveFormTemplate(formData, changes);
      setShowChangeConfirm(false);
    } catch (error) {
      setShowChangeConfirm(false);
    }
  };

  const handleDelete = async (e: any) => {
    e.preventDefault();
    if (!window.confirm(translate('resources.form_template.notification.confirm_delete'))) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await dataProvider.delete('form_template', {
        id: recordId,
        meta: {
          gqlQuery: `
            mutation RequestDeleteTemplate($template_id: Int!) {
              request_delete_template(params: {
                template_id: $template_id,
              }) {
                error
              }
            }
          `,
          variables: {
            template_id: Number(recordId),
          },
        },
      });

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      notify('resources.form_template.notification.deleted_success', { type: 'success' });
      redirect('list', 'form_template');
    } catch (error) {
      console.error('Delete error:', error);
      notify('resources.form_template.notification.deleted_error', { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoadingTemplate || isLoadingFields) return <Loading />;

  return (
    <Edit>
      <SimpleForm
        onSubmit={handleSubmit}
        toolbar={
          <Toolbar>
            <SaveButton disabled={isSubmitting} alwaysEnable={true} />
            <Button
              onClick={handleDelete}
              disabled={isDeleting}
              sx={{ bgcolor: 'error.main', color: 'white', marginLeft: '10px' }}
            >
              {translate('ra.action.delete')}
            </Button>
          </Toolbar>
        }
      >
        <FormTemplateEditContent
          fields={fields}
          deletedFields={deletedFields}
          setFields={setFields}
          setDeletedFields={setDeletedFields}
          onAnalyzeChanges={analyzeChanges}
          isDragEnabled={isDragEnabled}
          record={record}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
        />
      </SimpleForm>

      <Dialog open={showChangeConfirm} onClose={() => setShowChangeConfirm(false)}>
        <DialogTitle>{translate('simple.confirm_template_changes')}</DialogTitle>
        <DialogContent>
          {changes.filter((c) => c.action === 'rename').length > 0 && (
            <>
              <Typography variant="subtitle1">{translate('simple.renamed_fields')}</Typography>
              {changes
                .filter((c) => c.action === 'rename')
                .map((c, i) => (
                  <Typography key={i}>
                    - {c.originalKey} → {c.newKey}
                  </Typography>
                ))}
            </>
          )}

          {changes.filter((c) => c.action === 'delete').length > 0 && (
            <>
              <Typography variant="subtitle1">{translate('simple.deleted_fields')}</Typography>
              {changes
                .filter((c) => c.action === 'delete')
                .map((c, i) => (
                  <Typography key={i}>- {c.originalKey}</Typography>
                ))}
            </>
          )}

          {changes.filter((c) => c.action === 'add').length > 0 && (
            <>
              <Typography variant="subtitle1">{translate('simple.added_fields')}</Typography>
              {changes
                .filter((c) => c.action === 'add')
                .map((c, i) => (
                  <Typography key={i}>- {c.newKey}</Typography>
                ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChangeConfirm(false)}>{translate('ra.action.cancel')}</Button>
          <Button
            onClick={() => {
              setShowChangeConfirm(false);
              handleSaveConfirmed(template);
            }}
            color="primary"
          >
            {translate('ra.action.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Edit>
  );
};

export default FormTemplateEdit;
